// src/utils/googleMaps.ts
// Google Maps JavaScript API を動的ロードするユーティリティ。
// 参考(customermap.js)では <script> を body に append して読み込んでいたのを、
// Promiseで1回だけ読み込む形に移植。

let loadPromise: Promise<any> | null = null;

/**
 * Google Maps JS API をロードする（多重ロード防止）。
 * @param apiUrlTemplate init が返す URL テンプレート（`{0}` が APIキーのプレースホルダ）
 * @param apiKey APIキー
 * @returns window.google
 */
export function loadGoogleMaps(apiUrlTemplate: string, apiKey: string): Promise<any> {
  const w = window as any;
  if (w.google && w.google.maps) return Promise.resolve(w.google);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    try {
      // init応答に googleMapsApi が無い/空だと src が不正になり地図が出ないため、明示的に弾く。
      if (!apiUrlTemplate) {
        loadPromise = null;
        reject(new Error('Google MapsのURLが未設定です（init応答の googleMapsApi が空）。/api2/map/init のレスポンス項目名を確認してください'));
        return;
      }
      // `{0}` を APIキーに置換。テンプレートでなければそのまま使う。
      let src = apiUrlTemplate.includes('{0}')
        ? apiUrlTemplate.replace('{0}', apiKey ?? '')
        : apiUrlTemplate;
      // 独自 callback は onload で解決するため取り除く
      src = src.replace(/([?&])callback=[^&]*/, '$1').replace(/[?&]$/, '');

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const g = (window as any).google;
        if (g && g.maps) resolve(g);
        else {
          loadPromise = null;
          reject(new Error('Google Maps のロードに失敗しました'));
        }
      };
      script.onerror = () => {
        loadPromise = null;
        reject(new Error('Google Maps スクリプトの読み込みに失敗しました'));
      };
      document.body.appendChild(script);
    } catch (e) {
      loadPromise = null;
      reject(e);
    }
  });
  return loadPromise;
}

/** Google Maps が既に利用可能か。 */
export function isGoogleMapsReady(): boolean {
  const w = window as any;
  return !!(w.google && w.google.maps);
}

// ランク別ピン画像を用意しているランク名（参考: createIcon の switch と同一）。
const PIN_RANK_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

/**
 * ランク別のピン画像URLを返す。
 * 参考(customermap.js の createIcon)と同様に、ランクA〜Gは map_marker_{rank}.png、
 * それ以外（未設定含む）は既定の map_marker.png を使う。
 * 画像は public/images/googlemap 配下に配置し、base path は PUBLIC_URL で解決する。
 * @param rankname ランク名（A〜G 等）。未設定・対象外なら既定ピン。
 */
export function pinIconUrl(rankname?: string): string {
  const base = `${process.env.PUBLIC_URL || ''}/images/googlemap`;
  if (rankname && PIN_RANK_NAMES.includes(rankname)) {
    return `${base}/map_marker_${rankname}.png`;
  }
  return `${base}/map_marker.png`;
}
